import type { RouteObject } from 'react-router';

import { lazy, Suspense } from 'react';
import { Outlet, Navigate } from 'react-router';

import { DashboardLayout } from 'src/layouts/dashboard';

import { LoadingScreen } from 'src/components/loading-screen';

import { AuthGuard } from 'src/auth/guard';

import { usePathname } from '../hooks';

// ----------------------------------------------------------------------

// 공지사항
const NoticesPage = lazy(() => import('src/pages/dashboard/notices/list'));
const NoticeDetailPage = lazy(() => import('src/pages/dashboard/notices/details'));
const NoticeNewPage = lazy(() => import('src/pages/dashboard/notices/new'));
const NoticeEditPage = lazy(() => import('src/pages/dashboard/notices/edit'));
// 블로그
const BlogPage = lazy(() => import('src/pages/dashboard/blog/list'));
const BlogDetailPage = lazy(() => import('src/pages/dashboard/blog/details'));
const BlogNewPage = lazy(() => import('src/pages/dashboard/blog/new'));
const BlogEditPage = lazy(() => import('src/pages/dashboard/blog/edit'));
// 사용방법
const GuidePage = lazy(() => import('src/pages/dashboard/guide/list'));
const GuideDetailPage = lazy(() => import('src/pages/dashboard/guide/details'));
const GuideNewPage = lazy(() => import('src/pages/dashboard/guide/new'));
const GuideEditPage = lazy(() => import('src/pages/dashboard/guide/edit'));
// 이벤트
const EventPage = lazy(() => import('src/pages/dashboard/event/list'));
const EventDetailPage = lazy(() => import('src/pages/dashboard/event/details'));
const EventNewPage = lazy(() => import('src/pages/dashboard/event/new'));
const EventEditPage = lazy(() => import('src/pages/dashboard/event/edit'));
const TermsPage = lazy(() => import('src/pages/dashboard/terms/list'));
const TermsDetailPage = lazy(() => import('src/pages/dashboard/terms/details'));
const TermsNewPage = lazy(() => import('src/pages/dashboard/terms/new'));
const TermsEditPage = lazy(() => import('src/pages/dashboard/terms/edit'));
const NotiHisListPage = lazy(() => import('src/pages/dashboard/noti-his/list'));
const NotiHisNewPage = lazy(() => import('src/pages/dashboard/noti-his/new'));
const StatisticsListPage = lazy(() => import('src/pages/dashboard/statistics/list'));
const ColdMailListPage = lazy(() => import('src/pages/dashboard/email/list'));
const ColdMailNewPage = lazy(() => import('src/pages/dashboard/email/new'));

// ----------------------------------------------------------------------

function SuspenseOutlet() {
  const pathname = usePathname();
  return (
    <Suspense key={pathname} fallback={<LoadingScreen />}>
      <Outlet />
    </Suspense>
  );
}

const dashboardLayout = () => (
  <DashboardLayout>
    <SuspenseOutlet />
  </DashboardLayout>
);

export const dashboardRoutes: RouteObject[] = [
  {
    path: 'dashboard',
    element: <AuthGuard>{dashboardLayout()}</AuthGuard>,
    children: [
      { index: true, element: <Navigate to="statistics/list" replace /> },
      {
        path: 'notices',
        children: [
          { index: true, element: <NoticesPage /> },
          { path: 'list', element: <NoticesPage /> },
          { path: ':uid', element: <NoticeDetailPage /> },
          { path: ':uid/edit', element: <NoticeEditPage /> },
          { path: 'new', element: <NoticeNewPage /> },
        ],
      },
      {
        path: 'blog',
        children: [
          { index: true, element: <BlogPage /> },
          { path: 'list', element: <BlogPage /> },
          { path: ':uid', element: <BlogDetailPage /> },
          { path: ':uid/edit', element: <BlogEditPage /> },
          { path: 'new', element: <BlogNewPage /> },
        ],
      },
      {
        path: 'guide',
        children: [
          { index: true, element: <GuidePage /> },
          { path: 'list', element: <GuidePage /> },
          { path: ':uid', element: <GuideDetailPage /> },
          { path: ':uid/edit', element: <GuideEditPage /> },
          { path: 'new', element: <GuideNewPage /> },
        ],
      },
      {
        path: 'event',
        children: [
          { index: true, element: <EventPage /> },
          { path: 'list', element: <EventPage /> },
          { path: ':uid', element: <EventDetailPage /> },
          { path: ':uid/edit', element: <EventEditPage /> },
          { path: 'new', element: <EventNewPage /> },
        ],
      },
      {
        path: 'terms',
        children: [
          { index: true, element: <TermsPage /> },
          { path: 'list', element: <TermsPage /> },
          { path: ':uid', element: <TermsDetailPage /> },
          { path: ':uid/edit', element: <TermsEditPage /> },
          { path: 'new', element: <TermsNewPage /> },
        ],
      },
      {
        path: 'noti-his',
        children: [
          { index: true, element: <NotiHisListPage /> },
          { path: 'list', element: <NotiHisListPage /> },
          { path: 'new', element: <NotiHisNewPage /> },
        ],
      },
      {
        path: 'email',
        children: [
          { index: true, element: <ColdMailListPage /> },
          { path: 'list', element: <ColdMailListPage /> },
          { path: 'new', element: <ColdMailNewPage /> },
        ],
      },
      {
        path: 'statistics',
        children: [
          { index: true, element: <StatisticsListPage /> },
          { path: 'list', element: <StatisticsListPage /> },
        ],
      },
    ],
  },
];
